import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NotificationPanelService } from '@core/services';
import { SessionStorageService } from '@core/session-storage.service';
import { GridColumn } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';

import { NotificationStatus } from '@shared/enums/notification-status';
import { SecurityStatus } from '@shared/enums/security-status';
import { Client } from '@shared/models';
import {
  AppNotificationService,
  HeaderService,
  ModalService,
} from '@shared/services';
import { Collection } from '../../../admin/models';

import { SecurityCardTaxResultItem } from '../../models/security-card-tax-results-item';
import { TaxResultService } from '../../services/tax-result.service';

import { KeyValue } from '@angular/common';
import { CheckboxComponent } from '@appkit4/angular-components/checkbox';
import { CheckboxEvent } from '@shared/abstraction/checkbox/checkbox-event';
import { CardsGridComponent } from '@shared/modules/grid/components/cards-grid/cards-grid.component';
import { QueryOptions } from 'odata-query';
import { map, Observable, of } from 'rxjs';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';
import { TaxResultHeadersService } from '../../services/tax-result-headers.service';
import { TaxResultSessionService } from '../../services/tax-result-session.service';

@Component({
  standalone: false,
  selector: 'gstar-tax-result-page',
  templateUrl: './tax-result-page.component.html',
  styleUrls: ['./tax-result-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxResultPageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly taxResultHeadersService = inject(TaxResultHeadersService);
  private readonly taxResultService = inject(TaxResultService);
  private readonly taxResultSessionService = inject(TaxResultSessionService);
  private readonly notificationPanelService = inject(NotificationPanelService);
  private readonly headerService = inject(HeaderService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );

  selectedSecurityIds = signal<number[]>([]);
  securityStatusItems = SecurityStatus;
  securities = signal<Collection<SecurityCardTaxResultItem>>({
    total: 0,
    items: [],
  });

  destroyRef = inject(DestroyRef);
  selectAllMode = signal<boolean>(false);
  selectedCount = computed<number>(() =>
    this.selectAllMode()
      ? this.securities().total
      : this.selectedSecurityIds().length
  );
  headers = signal<GridColumn[]>([]);
  grid = viewChild<CardsGridComponent<SecurityCardTaxResultItem>>('grid');
  @ViewChild('selectAllCheckbox')
  selectAllCheckbox?: CheckboxComponent;
  private isApplyingCheckboxState = false;
  private lastFilterHash = '';

  get taxResult(): TaxResultService {
    return this.taxResultService;
  }

  get idsSecuritiesSelected(): number[] {
    return this.selectedSecurityIds();
  }

  constructor() {
    const taxResultHeadersService = this.taxResultHeadersService;

    this.headers.set(taxResultHeadersService.getHeaders());

    this.headerService.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((client: Client | undefined) => {
        this.clearSelection();
        this.updateSelectedClient(client);
      });
  }

  ngOnDestroy(): void {
    this.taxResultService.clearQuery();
  }

  ngOnInit(): void {
    this.taxResultService.taxResultByClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securities: Collection<SecurityCardTaxResultItem>) => {
        this.securities.set(securities);
        this.syncVisibleSelectionChecks();
      });
  }

  ngAfterViewInit(): void {
    const securitiesNames: string[] = this.sessionStorage
      .get('approved-successfully')
      .split(',')
      .filter((securityName: string) => securityName !== '');

    if (securitiesNames.length > 0) {
      this.notificationPanelService.reloadPendingApprovalCounter();
      this.notificationPanelService.reloadApprovedCounter();
      this.appNotificationService.notify(
        'notifications.approveTitleSuccess',
        'notifications.approveDescriptionSuccess',
        NotificationStatus.SUCCESS,
        {
          securitiesNames,
        }
      );

      this.sessionStorage.remove('approved-successfully');
    }

    this.syncVisibleSelectionChecks();
  }

  queryChangedHandler(
    query: Partial<QueryOptions<SecurityCardTaxResultItem>>
  ): void {
    const currentFilterHash = JSON.stringify(query.filter ?? {});
    if (currentFilterHash !== this.lastFilterHash) {
      this.clearSelection();
      this.lastFilterHash = currentFilterHash;
    }

    this.taxResultService.updateQuery(query);
  }

  onSelectFilterItem(item: KeyValue<string, SecurityStatus>): void {
    this.clearSelection();
    this.taxResultService.updateSecurityStatus(item.value);
    if (this.grid()) {
      this.grid()?.clearFiltersAndRun();
    }
  }

  onSecuritySelected(selection: SecurityCardTaxResultItem): void {
    const securityId = selection.securityId;

    if (securityId === undefined || securityId === null) {
      return;
    }

    if (this.selectAllMode() && !selection.isChecked) {
      this.openExitSelectAllModal(selection);
      return;
    }

    this.selectedSecurityIds.update((ids: number[]) => {
      const next = new Set(ids);
      if (selection.isChecked) {
        next.add(securityId);
      } else {
        next.delete(securityId);
      }
      return Array.from(next);
    });

    if (
      !this.selectAllMode() &&
      this.selectedSecurityIds().length === this.securities().total &&
      this.securities().total > 0
    ) {
      this.openSelectAllModal(false);
    }
  }

  editTaxResult(): void {
    this.resolveSelectionIds()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids: number[]) => {
        this.taxResultSessionService.selectSecuritiesIdsAndUpdate(ids);
        this.router.navigate(['tax-results/edit']);
      });
  }

  goToViewTaxResults(): void {
    this.resolveSelectionIds()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids: number[]) => {
        this.taxResultSessionService.selectSecuritiesIdsAndUpdate(ids);
        this.router.navigate(['tax-results/view'], {
          state: { securitiesIDs: ids },
        });
      });
  }

  private updateSelectedClient(client: Client | undefined): void {
    this.taxResultService.updateClient(client);
    if (!this.taxResultService.isFirst()) {
      this.investmentJurisdictionService.reloadCountryList();
    }
    if (this.grid()) {
      this.grid()?.clearFiltersAndRun();
    }
  }

  toggleAllSelections(checked: boolean): void {
    if (checked) {
      this.forceCheckboxVisualState(false);
      this.openSelectAllModal();
      return;
    }

    this.clearSelection();
  }

  onSelectAllCheckboxChange(event: CheckboxEvent): void {
    if (this.isApplyingCheckboxState) {
      return;
    }

    this.toggleAllSelections(event.checkboxState);
  }

  /**
   * Open a confirmation modal when the user tries to select all securities.
   */
  private openSelectAllModal(resetCheckboxOnCancel = true): void {
    const modal = this.modalService.open(
      ConfirmationModalComponent,
      {
        appModalId: 'selection-confirm-modal',
      },
      {
        title: this.translateService.instant(
          'portfolio.selectionModal.allTitle'
        ),
        content: this.translateService.instant(
          'portfolio.selectionModal.allContent',
          {
            count: this.securities().total,
          }
        ),
        modalId: 'selection-confirm-modal',
        confirmationFn: () => {
          this.selectAllMode.set(true);
          this.selectedSecurityIds.set([]);
          this.syncVisibleSelectionChecks();
          this.forceCheckboxVisualState(true);
          this.cdr.markForCheck();
        },
      }
    );

    modal.instance
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isConfirm: boolean) => {
        if (!isConfirm && resetCheckboxOnCancel) {
          this.forceCheckboxVisualState(false);
        }
      });
  }

  /**
   * Exits select all mode.
   * @param uncheckedSecurity
   */
  private openExitSelectAllModal(
    uncheckedSecurity: SecurityCardTaxResultItem
  ): void {
    const securityId = uncheckedSecurity.securityId as number;

    this.modalService.open(
      ConfirmationModalComponent,
      {
        appModalId: 'selection-confirm-modal',
      },
      {
        title: this.translateService.instant(
          'portfolio.selectionModal.singleTitle'
        ),
        content: this.translateService.instant(
          'portfolio.selectionModal.singleContent'
        ),
        modalId: 'selection-confirm-modal',
        confirmationFn: () => {
          this.selectAllMode.set(false);

          this.taxResultService
            .loadAllSecurities(this.securities().total)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
              (securityCollection: Collection<SecurityCardTaxResultItem>) => {
                const allIds = securityCollection.items
                  .map(
                    (security: SecurityCardTaxResultItem) => security.securityId
                  )
                  .filter((id): id is number => id !== undefined && id !== null)
                  .filter((id: number) => id !== securityId);

                this.selectedSecurityIds.set(allIds);
                this.syncVisibleSelectionChecks();
                this.cdr.markForCheck();
              }
            );
        },
      }
    );
    this.syncVisibleSelectionChecks();
    this.cdr.markForCheck();
  }

  private syncVisibleSelectionChecks(): void {
    const selectedIds = new Set(this.selectedSecurityIds());
    let isSelectAllMode = this.selectAllMode();

    // Keep UI and state consistent: with zero total items, select-all cannot stay active.
    if (isSelectAllMode && this.securities().total === 0) {
      this.selectAllMode.set(false);
      isSelectAllMode = false;
    }

    this.securities.update(
      (securityCollection: Collection<SecurityCardTaxResultItem>) => ({
        ...securityCollection,
        items: securityCollection.items.map(
          (security: SecurityCardTaxResultItem) => {
            const securityId = security.securityId;
            const isChecked =
              isSelectAllMode ||
              (securityId !== undefined &&
                securityId !== null &&
                selectedIds.has(securityId));

            return {
              ...security,
              isChecked,
            };
          }
        ),
      })
    );

    const visibleItems = this.securities().items;
    const hasSelectedSecurities = isSelectAllMode
      ? this.securities().total > 0
      : this.selectedSecurityIds().length > 0;
    const shouldBeChecked =
      hasSelectedSecurities &&
      (isSelectAllMode ||
        (visibleItems.length > 0 &&
          visibleItems.every(
            (item: SecurityCardTaxResultItem) => item.isChecked
          )));

    this.forceCheckboxVisualState(shouldBeChecked);

    this.cdr.markForCheck();
  }

  private forceCheckboxVisualState(checked: boolean): void {
    const checkbox = this.selectAllCheckbox;
    if (!checkbox) {
      return;
    }

    if (checkbox.disabled) {
      checkbox.checked = checked;
      checkbox.indeterminate = false;
      this.cdr.markForCheck();
      return;
    }

    this.isApplyingCheckboxState = true;
    try {
      checkbox.setCheckboxState({
        checked,
        indeterminate: false,
        originEvent: new Event('change'),
      });
    } finally {
      this.isApplyingCheckboxState = false;
    }
  }

  private resolveSelectionIds(): Observable<number[]> {
    if (!this.selectAllMode()) {
      return of(this.selectedSecurityIds());
    }

    return this.taxResultService
      .loadAllSecurities(this.securities().total)
      .pipe(
        map((securityCollection: Collection<SecurityCardTaxResultItem>) =>
          securityCollection.items
            .map((security: SecurityCardTaxResultItem) => security.securityId)
            .filter((id): id is number => id !== undefined && id !== null)
        ),
        map((ids: number[]) => {
          this.selectedSecurityIds.set(ids);
          return ids;
        })
      );
  }

  private clearSelection(): void {
    this.selectAllMode.set(false);
    this.selectedSecurityIds.set([]);
    this.syncVisibleSelectionChecks();
    this.forceCheckboxVisualState(false);
  }
}
